import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Calendar, TrendingUp, Wallet, Target, Repeat, Smartphone } from "lucide-react";
import { supabase } from "@/lib/supabase";

// Microsoft Clarity — carregado APENAS na landing page (não grava dentro do app)
function useClarity() {
  useEffect(() => {
    if (window.clarity || document.getElementById("ms-clarity")) return;
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.id="ms-clarity";t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "xludxyhy5j");
  }, []);
}

function useReveal() {
  useEffect(() => {
    const timer = setTimeout(() => {
      const els = document.querySelectorAll(".reveal");
      if (!("IntersectionObserver" in window)) { els.forEach(el => el.classList.add("in")); return; }
      const io = new IntersectionObserver(
        entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
        { threshold: 0.05 }
      );
      els.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) el.classList.add("in");
        else io.observe(el);
      });
      return () => io.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.6 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

// Ambos os botões fazem login Google
// O ProtectedRoute cuida do resto: se tem assinatura → dashboard, se não → /planos
const handleLoginWithGoogle = async () => {
  await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: window.location.origin + "/dashboard" }
  });
};

const CSS = `
.lg-page{--ink:#0B1220;--sec:rgba(50,55,70,.64);--sec2:rgba(50,55,70,.42);--blue:#007AFF;--green:#248A3D;--greenB:#34C759;--red:#D70015;
  --glass:rgba(255,255,255,.55);--glass2:rgba(255,255,255,.38);--stroke:rgba(255,255,255,.72);--sep:rgba(60,60,67,.14);
  color:var(--ink);font-family:'Sora',-apple-system,BlinkMacSystemFont,'SF Pro Text',sans-serif;
  background:linear-gradient(180deg,#EAF1FB 0%,#F0EEF9 45%,#EBF3F1 100%);min-height:100vh;overflow-x:hidden;position:relative}
.lg-blobs{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
.lg-blob{position:absolute;border-radius:50%;filter:blur(80px)}
.lg-page *{box-sizing:border-box}
.lg-wrap{max-width:1140px;margin:0 auto;padding:0 20px;position:relative;z-index:1}
.lg-card{background:var(--glass);border:1px solid var(--stroke);border-radius:24px;
  backdrop-filter:blur(26px) saturate(180%);-webkit-backdrop-filter:blur(26px) saturate(180%);
  box-shadow:0 12px 36px rgba(40,60,110,.13),inset 0 1px 0 rgba(255,255,255,.9)}
.lg-btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;padding:15px 26px;min-height:52px;border:none;border-radius:16px;
  font-size:16px;font-weight:700;cursor:pointer;font-family:inherit;text-decoration:none;transition:transform .15s}
.lg-btn:active{transform:scale(.97)}
.lg-btn-blue{color:#fff;background:linear-gradient(180deg,#2E96FF,#0071E8);
  box-shadow:0 10px 26px rgba(0,113,232,.35),inset 0 1.5px 0 rgba(255,255,255,.5)}
.lg-btn-glass{background:var(--glass);color:var(--blue);border:1px solid var(--stroke);
  backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);
  box-shadow:0 8px 22px rgba(40,60,110,.12),inset 0 1px 0 rgba(255,255,255,.9)}
.lg-eyebrow{font-size:13px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--blue)}
.lg-h2{font-size:clamp(26px,5vw,40px);font-weight:800;letter-spacing:-.02em;line-height:1.12;margin:10px 0 12px}
.lg-sub{font-size:17px;line-height:1.6;color:var(--sec);max-width:620px}
.reveal{opacity:0;transform:translateY(18px);transition:opacity .6s ease,transform .6s ease}
.reveal.in{opacity:1;transform:none}
@media(prefers-reduced-motion:reduce){.reveal{opacity:1;transform:none;transition:none}}
.lg-num{font-variant-numeric:tabular-nums}
.lg-grid3{display:grid;gap:16px;grid-template-columns:1fr}
@media(min-width:760px){.lg-grid3{grid-template-columns:repeat(3,1fr)}}
.lg-grid2{display:grid;gap:16px;grid-template-columns:1fr}
@media(min-width:760px){.lg-grid2{grid-template-columns:1fr 1fr}}
.lg-icon{width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.4),0 4px 12px rgba(0,0,0,.15)}
.lg-faq summary{cursor:pointer;font-weight:700;font-size:16px;padding:18px 20px;list-style:none;display:flex;justify-content:space-between;align-items:center;min-height:56px}
.lg-faq summary::-webkit-details-marker{display:none}
.lg-faq summary::after{content:"＋";color:var(--blue);font-weight:800}
.lg-faq[open] summary::after{content:"−"}
.lg-faq p{padding:0 20px 18px;color:var(--sec);font-size:15px;line-height:1.6;margin:0}
`;

function Logo({ size = 16 }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: `${size * 0.38}px ${size * 0.85}px`, borderRadius: 99,
      background: "rgba(255,255,255,.55)", border: "1px solid rgba(255,255,255,.72)",
      backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)",
      boxShadow: "0 6px 18px rgba(40,60,110,.14), inset 0 1.5px 0 rgba(255,255,255,.95)" }}>
      <b style={{ fontWeight: 800, letterSpacing: "-.02em", fontSize: size, whiteSpace: "nowrap",
        background: "linear-gradient(180deg,#16203A 20%,#41537A 90%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
        Conta em Dia
      </b>
    </span>
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    fn(); window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <header style={{
      position: "fixed", insetInline: 0, top: 0, zIndex: 50, transition: "all .3s",
      background: scrolled ? "rgba(255,255,255,.6)" : "transparent",
      backdropFilter: scrolled ? "blur(24px) saturate(180%)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(24px) saturate(180%)" : "none",
      borderBottom: scrolled ? "1px solid rgba(255,255,255,.7)" : "1px solid transparent",
    }}>
      <div className="lg-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
        <a href="#top" style={{ textDecoration: "none" }}><Logo size={15} /></a>
        <nav style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <a href="#como-funciona" className="lg-nav" style={{ textDecoration: "none", color: "var(--sec)", fontSize: 14, fontWeight: 600 }}>Como funciona</a>
          <a href="#recursos" style={{ textDecoration: "none", color: "var(--sec)", fontSize: 14, fontWeight: 600 }}>Recursos</a>
          <button onClick={handleLoginWithGoogle} className="lg-btn lg-btn-blue" style={{ padding: "9px 16px", minHeight: 40, fontSize: 14 }}>Entrar</button>
        </nav>
      </div>
    </header>
  );
}

// Mock do produto: dashboard em vidro
function Mockup() {
  const caixinhas = [
    ["M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6l7-3z", "Emergência", "R$ 18.893"],
    ["M4 19h16M6.5 15.5l3.5-4.5 3 2.5 4.5-6", "Investimento", "R$ 34.251"],
    ["M5 9h11v6a4 4 0 01-4 4H9a4 4 0 01-4-4V9zM16 10h1.5a2.5 2.5 0 010 5H16M8 5.5V4M11 5.5V4", "Dia a dia", "R$ 4.649"],
  ];
  const dias = [
    ["Hoje", "+R$ 2,40", "R$ 4.690", false],
    ["Dia 5", "+R$ 7.145", "R$ 10.483", true],
    ["Dia 8", "−R$ 355", "R$ 9.549", true],
  ];
  return (
    <div style={{ position: "relative", maxWidth: 460, margin: "0 auto" }}>
      <div className="lg-card" style={{ padding: 20, borderRadius: 30 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--sec)" }}>Patrimônio total</div>
        <div className="lg-num" style={{ fontSize: 34, fontWeight: 800, margin: "2px 0 2px" }}>R$ 75.152,88</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--green)" }}>▲ R$ 35,40 hoje · sincronizado</div>
        <svg viewBox="0 0 300 60" style={{ width: "100%", height: 56, marginTop: 10 }}>
          <defs><linearGradient id="lgg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34C759" stopOpacity=".25"/><stop offset="100%" stopColor="#34C759" stopOpacity="0"/>
          </linearGradient></defs>
          <path d="M4,48 L40,44 L76,45 L112,38 L148,34 L184,36 L220,24 L256,20 L296,10 L296,60 L4,60 Z" fill="url(#lgg)"/>
          <path d="M4,48 L40,44 L76,45 L112,38 L148,34 L184,36 L220,24 L256,20 L296,10" fill="none" stroke="#34C759" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
        <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
          {caixinhas.map(([d, n, v]) => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,.5)", border: "1px solid var(--stroke)", borderRadius: 14, padding: "9px 12px" }}>
              <span style={{ width: 30, height: 30, borderRadius: 9, background: "rgba(255,255,255,.65)", border: "1px solid var(--stroke)", display: "flex", alignItems: "center", justifyContent: "center", color: "#16203A" }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d={d} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <span style={{ fontSize: 14, fontWeight: 650 }}>{n}</span>
              <span className="lg-num" style={{ marginLeft: "auto", fontSize: 14, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="lg-card" style={{ position: "absolute", right: -14, bottom: -26, width: 230, padding: 14, borderRadius: 20, boxShadow: "0 18px 44px rgba(40,60,110,.22), inset 0 1px 0 rgba(255,255,255,.9)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--sec)" }}>Fluxo · projeção</div>
        {dias.map(([d, mv, s, fut]) => (
          <div key={d} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "7px 0", borderBottom: "1px solid var(--sep)", opacity: fut ? .62 : 1 }}>
            <span style={{ fontWeight: 700 }}>{d}</span>
            <span className="lg-num" style={{ color: mv.startsWith("+") ? "var(--green)" : "var(--red)", fontWeight: 650 }}>{mv}</span>
            <span className="lg-num" style={{ fontWeight: 700 }}>{s}</span>
          </div>
        ))}
        <div style={{ fontSize: 11, color: "var(--sec)", paddingTop: 7 }}>menor saldo previsto: R$ 3.918 em 31/08</div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  useClarity();
  useReveal();

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { data: sub } = await supabase
          .from("subscriptions").select("status,trial_ends_at")
          .eq("user_id", session.user.id).maybeSingle();
        const isActive = sub?.status === "active";
        const isTrial = sub?.status === "trial" && sub?.trial_ends_at && new Date() < new Date(sub.trial_ends_at);
        if (isActive || isTrial) navigate("/dashboard", { replace: true });
        else setLoading(false);
      } else setLoading(false);
    });
  }, [navigate]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#EAF1FB,#EBF3F1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: "2.5px solid #007AFF", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const passos = [
    { n: "1", t: "Abra o app do banco e digite os saldos", d: "De manhã, você confere as caixinhas no app do banco e digita os valores aqui. Menos de um minuto." },
    { n: "2", t: "O Conta em Dia calcula tudo sozinho", d: "Rendimentos, retiradas e o saldo do dia, sem nenhuma fórmula. O fluxo de caixa sincroniza sozinho." },
    { n: "3", t: "Veja quanto vai sobrar amanhã", d: "A projeção de 30, 60 e 90 dias já considera seus gastos planejados. Se o saldo for apertar, você descobre antes." },
  ];

  const recursos = [
    { I: Wallet, cor: "#AF52DE", t: "Caixinhas com metas", d: "Cada reserva com meta anual, progresso e a previsão de quando você chega lá." },
    { I: Calendar, cor: "#007AFF", t: "Fluxo de caixa diário", d: "O saldo de cada dia, real e projetado, já contando salário, contas fixas e seu gasto médio." },
    { I: TrendingUp, cor: "#34C759", t: "Análise de rendimentos", d: "Escolha qualquer período e veja quanto rendeu, quanto saiu e o que ficou, caixinha por caixinha." },
    { I: Target, cor: "#FF9500", t: "Custos sob controle", d: "Gastos por categoria, mês a mês. Quando alguma estoura o orçamento, fica vermelho." },
    { I: Repeat, cor: "#30B0C7", t: "Uma atualização, tudo em dia", d: "Um botão atualiza caixinhas, fluxo e histórico de uma vez. O trabalho de duas planilhas, em um toque." },
    { I: Smartphone, cor: "#FF2D55", t: "No celular e no computador", d: "Entre com sua conta Google e continue de onde parou, em qualquer tela. Tudo salvo na nuvem." },
  ];

  const faq = [
    ["Preciso conectar minha conta bancária?", "Não. Você digita os saldos que vê no app do seu banco. Sem senha bancária, sem Open Finance. Seus dados ficam na sua conta, protegidos pelo login do Google."],
    ["Substitui mesmo a planilha?", "Sim. O Conta em Dia nasceu de duas planilhas usadas de verdade, todo dia útil: uma de caixinhas e custos, outra de projeção de caixa. O app une as duas e faz as contas sozinho."],
    ["Quanto tempo leva a atualização diária?", "Menos de um minuto. Você digita os saldos e o app calcula rendimentos, saídas, o saldo do dia e a projeção dos próximos meses."],
    ["Posso cancelar quando quiser?", "Pode. A assinatura é mensal, sem fidelidade. O cancelamento fica na página de planos, a dois toques."],
  ];

  return (
    <div className="lg-page">
      <style>{CSS}</style>
      <div className="lg-blobs" aria-hidden>
        <div className="lg-blob" style={{ width: 480, height: 480, background: "#AFCBFF", top: -140, left: -100, opacity: .6 }} />
        <div className="lg-blob" style={{ width: 420, height: 420, background: "#D9C7FF", top: "26%", right: -150, opacity: .55 }} />
        <div className="lg-blob" style={{ width: 500, height: 500, background: "#B6F0DC", bottom: -160, left: -80, opacity: .55 }} />
      </div>
      <Header />
      <main>
        {/* HERO */}
        <section id="top" style={{ paddingTop: 120, paddingBottom: 70 }}>
          <div className="lg-wrap">
            <div className="lg-grid2" style={{ alignItems: "center", gap: 40 }}>
              <div className="reveal">
                <h1 style={{ fontSize: "clamp(34px,7vw,58px)", fontWeight: 800, letterSpacing: "-.025em", lineHeight: 1.06, margin: 0 }}>
                  Saiba quanto você tem.<br />
                  <span style={{ background: "linear-gradient(90deg,#0071E8,#34C759)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>E quanto vai ter.</span>
                </h1>
                <p className="lg-sub" style={{ marginTop: 18 }}>
                  Planilhas mostram onde o dinheiro foi. O <b style={{ color: "var(--ink)" }}>Conta em Dia</b> mostra o saldo de cada caixinha hoje e o que vai sobrar nos próximos 30, 60 e 90 dias. Um minuto por dia mantém tudo em ordem.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 26 }}>
                  <button onClick={handleLoginWithGoogle} className="lg-btn lg-btn-blue"><GoogleIcon /> Começar com Google</button>
                  <a href="#como-funciona" className="lg-btn lg-btn-glass">Como funciona <ArrowRight size={17} /></a>
                </div>
                <p style={{ marginTop: 12, fontSize: 12.5, color: "var(--sec2)" }}>Login com Google · R$ 19,90/mês · Cancele quando quiser</p>
              </div>
              <div className="reveal" style={{ transitionDelay: "120ms", paddingBottom: 30 }}><Mockup /></div>
            </div>
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section id="como-funciona" style={{ padding: "60px 0" }}>
          <div className="lg-wrap">
            <div className="reveal" style={{ textAlign: "center", marginBottom: 34 }}>
              <div className="lg-eyebrow">Como funciona</div>
              <h2 className="lg-h2">Sua rotina de sempre.<br />Sem o trabalho de sempre.</h2>
              <p className="lg-sub" style={{ margin: "0 auto" }}>Feito para quem confere as finanças toda manhã e cansou de manter planilha na mão.</p>
            </div>
            <div className="lg-grid3">
              {passos.map((p, i) => (
                <div key={p.n} className="lg-card reveal" style={{ padding: 26, transitionDelay: `${i * 90}ms` }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(180deg,#2E96FF,#0071E8)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, boxShadow: "0 6px 16px rgba(0,113,232,.3)" }}>{p.n}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 750, margin: "14px 0 8px" }}>{p.t}</h3>
                  <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--sec)", margin: 0 }}>{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RECURSOS */}
        <section id="recursos" style={{ padding: "60px 0" }}>
          <div className="lg-wrap">
            <div className="reveal" style={{ textAlign: "center", marginBottom: 34 }}>
              <div className="lg-eyebrow">Recursos</div>
              <h2 className="lg-h2">Tudo o que as duas planilhas faziam.<br />E o que elas nunca conseguiram.</h2>
            </div>
            <div className="lg-grid3">
              {recursos.map((r, i) => (
                <div key={r.t} className="lg-card reveal" style={{ padding: 24, transitionDelay: `${i * 70}ms` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="lg-icon" style={{ background: r.cor }}><r.I size={21} /></div>
                    <h3 style={{ fontSize: 16.5, fontWeight: 750, margin: 0 }}>{r.t}</h3>
                  </div>
                  <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "var(--sec)", margin: "12px 0 0" }}>{r.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PLANO */}
        <section id="planos" style={{ padding: "60px 0" }}>
          <div className="lg-wrap">
            <div className="lg-card reveal" style={{ maxWidth: 520, margin: "0 auto", padding: 32, textAlign: "center", borderRadius: 30 }}>
              <div className="lg-eyebrow">Plano único</div>
              <div style={{ fontSize: 44, fontWeight: 800, margin: "10px 0 2px" }} className="lg-num">R$ 19,90<span style={{ fontSize: 17, fontWeight: 650, color: "var(--sec)" }}>/mês</span></div>
              <p style={{ fontSize: 14.5, color: "var(--sec)", margin: "0 0 18px" }}>Tudo incluído. Sem fidelidade.</p>
              <div style={{ display: "grid", gap: 10, textAlign: "left", maxWidth: 340, margin: "0 auto 22px" }}>
                {["Caixinhas, fluxo, custos e análises", "Projeção de saldo de 30 a 90 dias", "Dados na nuvem, no celular e no computador", "Cancele quando quiser"].map((t) => (
                  <div key={t} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 15, fontWeight: 600 }}>
                    <Check size={18} color="#248A3D" strokeWidth={3} /> {t}
                  </div>
                ))}
              </div>
              <button onClick={handleLoginWithGoogle} className="lg-btn lg-btn-blue" style={{ width: "100%" }}><GoogleIcon /> Começar com Google</button>
              <a href="/planos" style={{ display: "inline-block", marginTop: 14, fontSize: 14, fontWeight: 650, color: "var(--blue)", textDecoration: "none" }}>Ver detalhes do plano →</a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ padding: "60px 0 40px" }}>
          <div className="lg-wrap" style={{ maxWidth: 720 }}>
            <div className="reveal" style={{ textAlign: "center", marginBottom: 26 }}>
              <div className="lg-eyebrow">Dúvidas frequentes</div>
              <h2 className="lg-h2">Antes de começar</h2>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {faq.map(([q, a], i) => (
                <details key={q} className="lg-card lg-faq reveal" style={{ borderRadius: 18, transitionDelay: `${i * 60}ms` }}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section style={{ padding: "40px 0 80px" }}>
          <div className="lg-wrap">
            <div className="lg-card reveal" style={{ padding: "44px 28px", textAlign: "center", borderRadius: 30, background: "linear-gradient(180deg,rgba(255,255,255,.65),rgba(230,240,255,.55))" }}>
              <h2 className="lg-h2" style={{ margin: "0 0 10px" }}>Amanhã de manhã, teste a diferença.</h2>
              <p className="lg-sub" style={{ margin: "0 auto 24px" }}>Um minuto para atualizar. O resto do dia sabendo exatamente onde você está e para onde vai.</p>
              <button onClick={handleLoginWithGoogle} className="lg-btn lg-btn-blue"><GoogleIcon /> Começar com Google</button>
            </div>
          </div>
        </section>
      </main>

      <footer style={{ borderTop: "1px solid var(--sep)", padding: "26px 0 90px", position: "relative", zIndex: 1 }}>
        <div className="lg-wrap" style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", justifyContent: "space-between" }}>
          <Logo size={13} />
          <div style={{ display: "flex", gap: 20, fontSize: 13.5, fontWeight: 600 }}>
            <a href="/planos" style={{ color: "var(--sec)", textDecoration: "none" }}>Planos</a>
            <a href="/privacidade" style={{ color: "var(--sec)", textDecoration: "none" }}>Privacidade</a>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--sec2)" }}>© {new Date().getFullYear()} Conta em Dia</div>
        </div>
      </footer>
    </div>
  );
}
